<!-- eslint-disable max-len -->
<template>
  <v-card>
    <v-card-title>
      <v-icon left>mdi-calendar-range</v-icon>
      Billing Period Selection
    </v-card-title>

    <v-card-text>
      <v-form ref="form" v-model="valid">
        <!-- Date Range Selection -->
        <v-row>
          <v-col cols="12" md="6">
            <v-menu v-model="startDateMenu" :close-on-content-click="false" :nudge-right="40"
              transition="scale-transition" offset-y min-width="auto">
              <template #activator="{ on, attrs }">
                <v-text-field v-model="formattedStartDate" label="Billing Start Date" prepend-icon="mdi-calendar"
                  readonly :rules="startDateRules" v-bind="attrs" v-on="on" />
              </template>
              <v-date-picker v-model="billingPeriod.startDate" @input="startDateMenu = false" />
            </v-menu>
          </v-col>

          <v-col cols="12" md="6">
            <v-menu v-model="endDateMenu" :close-on-content-click="false" :nudge-right="40"
              transition="scale-transition" offset-y min-width="auto">
              <template #activator="{ on, attrs }">
                <v-text-field v-model="formattedEndDate" label="Billing End Date" prepend-icon="mdi-calendar" readonly
                  :rules="endDateRules" v-bind="attrs" v-on="on" />
              </template>
              <v-date-picker v-model="billingPeriod.endDate" @input="endDateMenu = false" />
            </v-menu>
          </v-col>
        </v-row>

        <!-- Data Entry Options -->
        <v-row>
          <v-col cols="12">
            <v-card outlined class="mt-4">
              <v-card-title class="subtitle-1">
                <v-icon left color="primary">mdi-database</v-icon>
                {{ isDGMode ? 'DG' : 'Electricity' }} Consumption Data Entry
              </v-card-title>

              <v-card-text>
                <v-row>
                  <v-col cols="12">
                    <v-radio-group v-model="dataEntryMode" row @change="onDataEntryModeChange">
                      <v-radio label="Automatic (from system)" value="automatic"
                        :disabled="!billingPeriod.startDate || !billingPeriod.endDate" />
                      <v-radio label="Manual entry" value="manual" />
                    </v-radio-group>
                  </v-col>
                </v-row>

                <!-- Automatic Data Retrieval -->
                <v-row v-if="dataEntryMode === 'automatic'">
                  <v-col cols="12">
                    <v-alert type="info" text dense class="mb-4">
                      <div class="subtitle-2">Automatic Data Retrieval</div>
                      <div>
                        System will automatically fetch
                        {{ isDGMode ? 'DG' : 'electricity' }}
                        consumption data from the plant energy monitoring
                        system for the selected period.
                      </div>
                    </v-alert>

                    <v-btn color="primary"
                      :disabled="!valid || !billingPeriod.startDate || !billingPeriod.endDate || loading"
                      :loading="loading" @click="fetchAutomaticConsumptionData">
                      <v-icon left>mdi-download</v-icon>
                      Fetch Consumption Data
                    </v-btn>
                  </v-col>
                </v-row>

                <!-- Manual Data Entry -->
                <v-row v-if="dataEntryMode === 'manual'">
                  <v-col cols="12">
                    <v-alert type="warning" text dense class="mb-4">
                      <div class="subtitle-2">Manual Data Entry</div>
                      <div>
                        Enter {{ isDGMode ? 'DG' : 'electricity' }}
                        meter readings manually. Ensure readings are accurate
                        as they will be used for billing calculations.
                      </div>
                    </v-alert>
                  </v-col>

                  <template v-if="isTimeOfUseMode">
                    <v-col cols="12">
                      <v-card outlined class="time-of-use-consumption-card">
                        <v-card-text class="pt-0">
                          <v-simple-table class="time-of-use-consumption-table">
                            <template #default>
                              <thead>
                                <tr>
                                  <th class="text-left">Unit Consumption</th>
                                  <th class="text-center">Initial</th>
                                  <th class="text-center">Final</th>
                                  <th class="text-center">Units</th>
                                </tr>
                              </thead>

                              <tbody>
                                <tr v-for="line in timeOfUseUnitLines" :key="line.key">
                                  <td class="unit-label-cell">
                                    <div class="font-weight-medium">{{ line.label }}</div>
                                  </td>

                                  <td>
                                    <v-text-field
                                      v-model.number="manualConsumptionData.timeOfUseUnits[line.key].initialReading"
                                      type="number" step="0.01" min="0" outlined dense hide-details
                                      class="time-of-use-table-input" @input="syncTimeOfUseUnitLine(line.key)" />
                                  </td>

                                  <td>
                                    <v-text-field
                                      v-model.number="manualConsumptionData.timeOfUseUnits[line.key].finalReading"
                                      type="number" step="0.01" min="0" outlined dense hide-details
                                      class="time-of-use-table-input" @input="syncTimeOfUseUnitLine(line.key)" />
                                  </td>

                                  <td class="text-center font-weight-bold">
                                    {{ formatTimeOfUseUnits(line.key) }}
                                  </td>
                                </tr>
                              </tbody>
                            </template>
                          </v-simple-table>

                          <v-divider class="my-4" />

                          <v-row align="center" class="time-of-use-total-row">
                            <v-col cols="12" md="8" class="py-0">
                              <div class="text-subtitle-2 font-weight-medium">
                                Total units are derived from the final and initial readings above.
                              </div>
                              <div class="caption text--secondary">
                                Difference is calculated as final minus initial reading for each line.
                              </div>
                            </v-col>

                            <v-col cols="12" md="4" class="py-0 text-md-right">
                              <v-chip
                                color="primary"
                                small
                                label
                                outlined
                                class="font-weight-bold mb-2"
                              >
                                TOTAL CONSUMPTION
                              </v-chip>
                              <div class="text-h6 font-weight-bold mt-1">
                                {{ formattedTimeOfUseTotalUnits }} kWh
                              </div>
                            </v-col>
                          </v-row>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </template>

                  <template v-else>
                    <v-col cols="12" md="4">
                      <v-text-field v-model.number="manualConsumptionData.initialReading"
                        :label="`Initial Reading (${isDGMode ? 'DG kWh' : 'kWh'})`" prepend-icon="mdi-gauge-empty"
                        type="number" step="0.1" min="0" :rules="readingRules" outlined @input="calculateConsumption" />
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field v-model.number="manualConsumptionData.finalReading"
                        :label="`Final Reading (${isDGMode ? 'DG kWh' : 'kWh'})`" prepend-icon="mdi-gauge-full"
                        type="number" step="0.1" min="0" :rules="readingRules" outlined @input="calculateConsumption" />
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field :value="calculatedConsumption"
                        :label="`Total Consumption (${isDGMode ? 'DG kWh' : 'kWh'})`" prepend-icon="mdi-lightning-bolt"
                        readonly outlined class="font-weight-bold"
                        :color="calculatedConsumption > 0 ? 'success' : 'grey'" />
                    </v-col>
                  </template>

                  <v-col cols="12" class="text-right">
                    <v-btn color="primary"
                      :disabled="!valid || !billingPeriod.startDate || !billingPeriod.endDate || calculatedConsumption <= 0"
                      @click="confirmManualConsumptionData">
                      <v-icon left>mdi-check</v-icon>
                      Confirm Manual Data
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Consumption Data Display -->
        <v-row v-if="consumptionData">
          <v-col cols="12">
            <v-card outlined class="mt-4">
              <v-card-title class="subtitle-1">
                <v-icon left color="success">mdi-check-circle</v-icon>
                Consumption Data Retrieved
                <v-spacer />
                <v-chip :color="consumptionData.dataSource === 'automatic' ? 'success' : 'warning'" text-color="white"
                  small>
                  <v-icon left small>
                    {{ consumptionData.dataSource === 'automatic' ? 'mdi-download' : 'mdi-pencil' }}
                  </v-icon>
                  {{ consumptionData.dataSource === 'automatic' ? 'Automatic' : 'Manual' }}
                </v-chip>
              </v-card-title>

              <v-card-text>
                <v-row v-if="consumptionData.hasActualReadings">
                  <v-col cols="12" md="4">
                    <v-text-field :value="consumptionData.initialReading"
                      :label="`Initial Reading (${isDGMode ? 'DG kWh' : 'kWh'})`" prepend-icon="mdi-gauge-empty"
                      readonly outlined />
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field :value="consumptionData.finalReading"
                      :label="`Final Reading (${isDGMode ? 'DG kWh' : 'kWh'})`" prepend-icon="mdi-gauge-full" readonly
                      outlined />
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field :value="consumptionData.totalConsumption"
                      :label="`Total Consumption (${isDGMode ? 'DG kWh' : 'kWh'})`" prepend-icon="mdi-lightning-bolt"
                      readonly outlined class="font-weight-bold" />
                  </v-col>
                </v-row>

                <v-row v-else>
                  <v-col cols="12" md="6" offset-md="3">
                    <v-text-field :value="consumptionData.totalConsumption"
                      :label="`Total Consumption (${isDGMode ? 'DG kWh' : 'kWh'})`" prepend-icon="mdi-lightning-bolt"
                      readonly outlined class="font-weight-bold text-center" />
                  </v-col>

                  <v-col cols="12">
                    <v-alert type="info" text dense>
                      <v-icon left small>mdi-information</v-icon>
                      Individual meter readings are not required in automatic
                      mode. The system provides total consumption directly
                      from the plant energy monitoring system.
                    </v-alert>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12">
                    <v-alert type="info" text dense class="mb-0">
                      <div class="subtitle-2">Billing Period Summary</div>
                      <div>
                        <strong>Period:</strong>
                        {{ formattedStartDate }} to {{ formattedEndDate }}
                      </div>
                      <div>
                        <strong>Duration:</strong> {{ billingDurationDays }} days
                      </div>
                      <div>
                        <strong>Average Daily Consumption:</strong>
                        {{ averageDailyConsumption }} kWh/day
                      </div>
                      <div>
                        <strong>Data Source:</strong> {{ consumptionDataSourceLabel }}
                      </div>
                    </v-alert>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Error Display -->
        <v-row v-if="error">
          <v-col cols="12">
            <v-alert type="error" dismissible @input="error = null">
              {{ error }}
            </v-alert>
          </v-col>
        </v-row>

        <!-- Global Confirm Dialog -->
        <v-dialog v-model="confirmDialog.show" max-width="520" persistent>
          <v-card>
            <v-card-title class="headline">
              <v-icon left :color="confirmDialog.iconColor">
                {{ confirmDialog.icon }}
              </v-icon>
              {{ confirmDialog.title }}
            </v-card-title>

            <v-card-text style="white-space: pre-line">
              {{ confirmDialog.message }}
            </v-card-text>

            <v-card-actions>
              <v-spacer />
              <v-btn text @click="handleDialogCancel">
                Cancel
              </v-btn>
              <v-btn color="primary" @click="handleDialogConfirm">
                OK
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
/* eslint-disable max-len */
import appConfig from '../../config/appConfig';
import costCategoryMixin from '../../mixins/costCategoryMixin';
import { resolveBillingMode, isTimeOfUseBillingMode } from '../../utils/billingMode';

export default {
  name: 'BillingPeriodSelector',
  mixins: [costCategoryMixin],

  props: {
    editMode: {
      type: Boolean,
      default: false,
    },
    initialData: {
      type: Object,
      default: null,
    },
    isDGMode: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      valid: false,
      loading: false,
      error: null,
      startDateMenu: false,
      endDateMenu: false,
      dataEntryMode: 'automatic',
      editModeInitializing: false,
      billingPeriod: {
        startDate: null,
        endDate: null,
      },
      consumptionData: null,
      manualConsumptionData: {
        initialReading: 0,
        finalReading: 0,
        timeOfUseUnits: this.createTimeOfUseUnits(),
      },
      confirmDialog: {
        show: false,
        title: '',
        message: '',
        icon: 'mdi-information',
        iconColor: 'primary',
        resolve: null,
      },
      startDateRules: [
        (v) => !!v || 'Start date is required',
      ],
      endDateRules: [
        (v) => !!v || 'End date is required',
      ],
      readingRules: [
        (v) => ((v !== null && v !== undefined && v !== '') || 'Reading is required'),
        (v) => !Number.isNaN(parseFloat(v)) || 'Reading must be a valid number',
        (v) => parseFloat(v) >= 0 || 'Reading must be non-negative',
      ],
    };
  },

  computed: {
    billingMode() {
      return resolveBillingMode({
        activeCategories: this.costCategories,
        consumptionData: this.consumptionData,
        manualData: {
          timeOfUseUnits: this.manualConsumptionData.timeOfUseUnits,
        },
      });
    },

    isTimeOfUseMode() {
      return !this.isDGMode && isTimeOfUseBillingMode(this.billingMode);
    },

    bypassFutureDateValidation() {
      return !this.isDGMode && appConfig.isFeatureEnabled('debugMode');
    },

    formattedStartDate() {
      return this.billingPeriod.startDate ? this.formatDate(this.billingPeriod.startDate) : '';
    },

    formattedEndDate() {
      return this.billingPeriod.endDate ? this.formatDate(this.billingPeriod.endDate) : '';
    },

    billingDurationDays() {
      if (!this.billingPeriod.startDate || !this.billingPeriod.endDate) return 0;
      const start = new Date(this.billingPeriod.startDate);
      const end = new Date(this.billingPeriod.endDate);
      const diffTime = Math.abs(end - start);
      return Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
    },

    averageDailyConsumption() {
      if (!this.consumptionData || this.billingDurationDays === 0) return 0;
      return Math.round(this.consumptionData.totalConsumption / this.billingDurationDays);
    },

    consumptionDataSourceLabel() {
      return this.consumptionData?.dataSource === 'automatic'
        ? 'Plant Energy Monitoring System'
        : 'Manual Entry';
    },

    calculatedConsumption() {
      if (this.isTimeOfUseMode && this.dataEntryMode === 'manual') {
        return this.timeOfUseTotalUnits;
      }

      const initial = parseFloat(this.manualConsumptionData.initialReading) || 0;
      const final = parseFloat(this.manualConsumptionData.finalReading) || 0;
      return final > initial ? final - initial : 0;
    },

    timeOfUseUnitLines() {
      return [
        { key: 'onPeak', label: 'ON PEAK', rate: 4.1025 },
        { key: 'offPeak', label: 'OFF PEAK', rate: 2.5849 },
        { key: 'sunday', label: 'SAT/SUNDAY', rate: 2.5849 },
        { key: 'misc', label: 'MISC UNITS', rate: 3.3437 },
      ];
    },

    timeOfUseTotalUnits() {
      return Object.entries(this.manualConsumptionData.timeOfUseUnits || {})
        .reduce((sum, [key, line]) => sum + this.getTimeOfUseLineUnits(key, line), 0);
    },

    formattedTimeOfUseTotalUnits() {
      return this.timeOfUseTotalUnits.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },

  watch: {
    'billingPeriod.startDate': {
      handler() {
        if (!this.editModeInitializing) {
          this.clearConfirmedConsumptionData();
          this.error = null;
        }
      },
    },

    'billingPeriod.endDate': {
      handler() {
        if (!this.editModeInitializing) {
          this.clearConfirmedConsumptionData();
          this.error = null;
        }
      },
    },

    manualConsumptionData: {
      deep: true,
      handler() {
        if (!this.editModeInitializing) {
          this.clearConfirmedConsumptionData();
        }
      },
    },
  },

  created() {
    if (!this.editMode) {
      this.loadExistingRecords();
    }

    if (this.editMode && this.initialData) {
      this.editModeInitializing = true;
      this.billingPeriod = {
        startDate: this.initialData.billingPeriod?.startDate || null,
        endDate: this.initialData.billingPeriod?.endDate || null,
      };

      if (this.initialData.consumptionData) {
        this.dataEntryMode = this.initialData.consumptionData.dataSource || 'manual';

        const timeOfUseUnits = this.normalizeTimeOfUseUnits(
          this.initialData.consumptionData.timeOfUseUnits
          || this.initialData.consumptionData.touBreakdown
          || {},
        );

        const timeOfUseTotalConsumption = Object.entries(timeOfUseUnits)
          .reduce((sum, [key, line]) => sum + this.getTimeOfUseLineUnits(key, line), 0);

        this.manualConsumptionData = {
          initialReading: this.initialData.consumptionData.initialReading || 0,
          finalReading: this.initialData.consumptionData.finalReading || 0,
          timeOfUseUnits,
        };

        const totalConsumption = this.isTimeOfUseMode && timeOfUseTotalConsumption > 0
          ? timeOfUseTotalConsumption
          : this.initialData.consumptionData.totalConsumption || timeOfUseTotalConsumption;

        this.consumptionData = {
          initialReading: this.initialData.consumptionData.initialReading,
          finalReading: this.initialData.consumptionData.finalReading,
          totalConsumption,
          billingPeriod: this.initialData.billingPeriod,
          dataSource: this.initialData.consumptionData.dataSource || 'manual',
          timeOfUseUnits,
          touBreakdown: timeOfUseUnits,
          demandBreakdown: this.initialData.consumptionData.demandBreakdown || {},
        };

        this.$nextTick(() => {
          this.editModeInitializing = false;

          this.$emit('consumption-data-retrieved', {
            billingPeriod: this.billingPeriod,
            consumptionData: this.consumptionData,
          });

          if (this.isDGMode) {
            this.$emit('dg-consumption-data-retrieved', {
              billingPeriod: this.billingPeriod,
              dgConsumptionData: this.consumptionData,
            });
          }
        });
      } else {
        this.$nextTick(() => {
          this.editModeInitializing = false;
        });
      }
    }
  },

  methods: {
    formatDate(date) {
      if (!date) return 'N/A';
      return this.$formatDate(date);
    },

    getBillingData() {
      return {
        billingPeriod: this.billingPeriod,
        consumptionData: this.consumptionData,
        isValid: this.valid && this.consumptionData !== null,
      };
    },

    reset() {
      this.billingPeriod = {
        startDate: null,
        endDate: null,
      };
      this.consumptionData = null;
      this.manualConsumptionData = {
        initialReading: 0,
        finalReading: 0,
        timeOfUseUnits: this.createTimeOfUseUnits(),
      };
      this.error = null;
      this.loading = false;

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    calculateConsumption() { },

    clearConfirmedConsumptionData() {
      if (!this.consumptionData) {
        return;
      }

      this.consumptionData = null;
      this.$emit('consumption-data-cleared');
    },

    createTimeOfUseUnits() {
      return {
        onPeak: {
          initialReading: 0,
          finalReading: 0,
          difference: 0,
          units: 0,
          rate: 4.1025,
        },
        offPeak: {
          initialReading: 0,
          finalReading: 0,
          difference: 0,
          units: 0,
          rate: 2.5849,
        },
        sunday: {
          initialReading: 0,
          finalReading: 0,
          difference: 0,
          units: 0,
          rate: 2.5849,
        },
        misc: {
          initialReading: 0,
          finalReading: 0,
          difference: 0,
          units: 0,
          rate: 3.3437,
        },
      };
    },

    getTimeOfUseLineUnits(key, line = {}) {
      const difference = parseFloat(line.difference);
      const units = parseFloat(line.units);

      if (!Number.isNaN(difference)) {
        return Math.max(difference, 0);
      }

      if (!Number.isNaN(units)) {
        return Math.max(units, 0);
      }

      const finalReading = parseFloat(line.finalReading) || 0;
      const initialReading = parseFloat(line.initialReading) || 0;
      return Math.max(finalReading - initialReading, 0);
    },

    getTimeOfUseLineRate(key, line = {}) {
      const rate = parseFloat(line.rate);

      if (!Number.isNaN(rate)) {
        return rate;
      }

      const matchingLine = this.timeOfUseUnitLines.find((item) => item.key === key);
      return matchingLine ? matchingLine.rate : 0;
    },

    syncTimeOfUseUnitLine(key) {
      const line = this.manualConsumptionData.timeOfUseUnits?.[key];

      if (!line) {
        return;
      }

      const finalReading = parseFloat(line.finalReading) || 0;
      const initialReading = parseFloat(line.initialReading) || 0;
      const difference = Math.max(finalReading - initialReading, 0);

      this.$set(this.manualConsumptionData.timeOfUseUnits, key, {
        ...line,
        finalReading,
        initialReading,
        difference,
        units: difference,
        rate: this.getTimeOfUseLineRate(key, line),
      });
    },

    formatTimeOfUseRate(rate) {
      return parseFloat(rate || 0).toLocaleString('en-US', {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      });
    },

    formatTimeOfUseDifference(key) {
      const line = this.manualConsumptionData.timeOfUseUnits?.[key] || {};
      return this.getTimeOfUseLineUnits(key, line).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formatTimeOfUseUnits(key) {
      return this.formatTimeOfUseDifference(key);
    },

    normalizeTimeOfUseUnits(units = {}) {
      return Object.fromEntries(
        Object.entries(units).map(([key, line]) => {
          const hasFinalReading = line?.finalReading !== undefined && line?.finalReading !== null && line?.finalReading !== '';
          const hasInitialReading = line?.initialReading !== undefined && line?.initialReading !== null && line?.initialReading !== '';
          const finalReading = hasFinalReading ? (parseFloat(line.finalReading) || 0) : 0;
          const initialReading = hasInitialReading ? (parseFloat(line.initialReading) || 0) : 0;
          const explicitUnits = parseFloat(line?.units);
          const explicitDifference = parseFloat(line?.difference);
          const calculatedDifference = hasFinalReading && hasInitialReading
            ? Math.max(finalReading - initialReading, 0)
            : NaN;

          let unitsValue = 0;

          if (!Number.isNaN(explicitUnits)) {
            unitsValue = Math.max(explicitUnits, 0);
          } else if (!Number.isNaN(explicitDifference)) {
            unitsValue = Math.max(explicitDifference, 0);
          } else if (!Number.isNaN(calculatedDifference)) {
            unitsValue = calculatedDifference;
          }

          const difference = !Number.isNaN(calculatedDifference) && calculatedDifference > 0
            ? calculatedDifference
            : unitsValue;

          const rate = this.getTimeOfUseLineRate(key, line);

          return [key, {
            ...line,
            finalReading,
            initialReading,
            difference,
            units: unitsValue,
            rate,
          }];
        }),
      );
    },

    async loadExistingRecords() {
      try {
        if (this.isDGMode) {
          await this.$store.dispatch('dgBilling/fetchDGBillingRecords');
        } else {
          await this.$store.dispatch('billing/fetchBillingRecords');
        }
      } catch (error) {
        console.error('⚠️ Failed to load existing records for overlap detection:', error);

        this.$emit('show-message', {
          type: 'warning',
          message: 'Could not load existing records for overlap detection. Please verify dates manually to avoid duplicates.',
        });
      }
    },

    onDataEntryModeChange() {
      this.consumptionData = null;
      this.error = null;

      if (this.dataEntryMode === 'automatic') {
        this.manualConsumptionData = {
          initialReading: 0,
          finalReading: 0,
          timeOfUseUnits: this.createTimeOfUseUnits(),
        };
      }
    },

    async fetchAutomaticConsumptionData() {
      if (!this.valid || !this.billingPeriod.startDate || !this.billingPeriod.endDate) {
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        const startDate = new Date(this.billingPeriod.startDate);
        startDate.setHours(0, 0, 0, 0);

        const endDate = new Date(this.billingPeriod.endDate);
        endDate.setHours(23, 59, 59, 999);

        if (startDate > endDate) {
          throw new Error('Start date must be before or equal to end date');
        }

        if (!this.bypassFutureDateValidation && endDate > new Date()) {
          throw new Error('End date cannot be in the future');
        }

        if (!this.editMode) {
          const overlapCheck = await this.checkForOverlappingRecords(startDate, endDate);
          if (overlapCheck.hasOverlap) {
            await this.handleOverlappingRecords(overlapCheck);
            return;
          }
        }

        let consumptionData;

        if (this.isDGMode) {
          consumptionData = await this.$store.dispatch('dgBilling/fetchDGConsumptionData', {
            startDate,
            endDate,
          });
        } else {
          consumptionData = await this.$store.dispatch('billing/fetchConsumptionData', {
            startDate,
            endDate,
          });
        }

        if (!consumptionData) {
          throw new Error('No consumption data received from the system');
        }

        this.consumptionData = {
          initialReading: consumptionData.initialReading || null,
          finalReading: consumptionData.finalReading || null,
          totalConsumption: consumptionData.totalConsumption || 0,
          billingPeriod: this.billingPeriod,
          dataSource: 'automatic',
          hasActualReadings: consumptionData.hasActualReadings !== false
            && consumptionData.initialReading !== null
            && consumptionData.finalReading !== null,
        };

        this.$emit('consumption-data-retrieved', {
          billingPeriod: this.billingPeriod,
          consumptionData: this.consumptionData,
        });

        if (this.isDGMode) {
          this.$emit('dg-consumption-data-retrieved', {
            billingPeriod: this.billingPeriod,
            dgConsumptionData: this.consumptionData,
          });
        }

        this.$emit('show-message', {
          type: 'success',
          message: `Consumption data automatically retrieved for ${this.billingDurationDays} days (${this.consumptionData.totalConsumption} kWh)`,
        });
      } catch (error) {
        console.error('Error fetching automatic consumption data:', error);
        this.error = error.message || 'Failed to fetch consumption data automatically';

        this.$emit('show-message', {
          type: 'error',
          message: `${this.error}. You can switch to manual entry mode.`,
        });

        const switchToManual = await this.showConfirmDialog(
          'Automatic Data Retrieval Failed',
          'Automatic data retrieval failed. Would you like to switch to manual entry mode?',
          'mdi-cloud-alert',
          'error',
        );

        if (switchToManual) {
          this.dataEntryMode = 'manual';
        }
      } finally {
        this.loading = false;
      }
    },

    async confirmManualConsumptionData() {
      if (!this.valid || !this.billingPeriod.startDate || !this.billingPeriod.endDate) {
        return;
      }

      if (this.calculatedConsumption <= 0) {
        this.$emit('show-message', {
          type: 'error',
          message: 'Final reading must be greater than initial reading',
        });
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        const startDate = new Date(this.billingPeriod.startDate);
        startDate.setHours(0, 0, 0, 0);

        const endDate = new Date(this.billingPeriod.endDate);
        endDate.setHours(23, 59, 59, 999);

        if (startDate > endDate) {
          throw new Error('Start date must be before or equal to end date');
        }

        if (!this.bypassFutureDateValidation && endDate > new Date()) {
          throw new Error('End date cannot be in the future');
        }

        if (!this.editMode) {
          const overlapCheck = await this.checkForOverlappingRecords(startDate, endDate);
          if (overlapCheck.hasOverlap) {
            await this.handleOverlappingRecords(overlapCheck);
            return;
          }
        }

        this.consumptionData = {
          initialReading: parseFloat(this.manualConsumptionData.initialReading),
          finalReading: parseFloat(this.manualConsumptionData.finalReading),
          totalConsumption: this.calculatedConsumption,
          billingPeriod: this.billingPeriod,
          dataSource: 'manual',
          hasActualReadings: true,
        };

        if (this.isTimeOfUseMode) {
          const timeOfUseUnits = this.normalizeTimeOfUseUnits(
            this.manualConsumptionData.timeOfUseUnits,
          );
          this.consumptionData.timeOfUseUnits = timeOfUseUnits;
          this.consumptionData.touBreakdown = timeOfUseUnits;
          this.consumptionData.totalConsumption = this.timeOfUseTotalUnits;
          this.consumptionData.initialReading = null;
          this.consumptionData.finalReading = null;
          this.consumptionData.hasActualReadings = false;
        }

        this.$emit('consumption-data-retrieved', {
          billingPeriod: this.billingPeriod,
          consumptionData: this.consumptionData,
        });

        if (this.isDGMode) {
          this.$emit('dg-consumption-data-retrieved', {
            billingPeriod: this.billingPeriod,
            dgConsumptionData: this.consumptionData,
          });
        }

        this.$emit('show-message', {
          type: 'success',
          message: `Manual consumption data confirmed for ${this.billingDurationDays} days`,
        });
      } catch (error) {
        console.error('Error confirming manual consumption data:', error);
        this.error = error.message || 'Failed to confirm consumption data';

        this.$emit('show-message', {
          type: 'error',
          message: this.error,
        });
      } finally {
        this.loading = false;
      }
    },

    async confirmConsumptionData() {
      if (this.dataEntryMode === 'automatic') {
        await this.fetchAutomaticConsumptionData();
      } else {
        await this.confirmManualConsumptionData();
      }
    },

    async checkForOverlappingRecords(startDate, endDate) {
      try {
        await this.loadExistingRecords();

        const existingRecords = this.isDGMode
          ? this.$store.getters['dgBilling/allDGBillingRecords'] || []
          : this.$store.getters['billing/allBillingRecords'] || [];

        const normalizeDate = (date) => {
          const d = new Date(date);
          return new Date(d.getFullYear(), d.getMonth(), d.getDate());
        };

        const newStart = normalizeDate(startDate);
        const newEnd = normalizeDate(endDate);

        const overlappingRecords = existingRecords.filter((record) => {
          if (this.editMode && this.initialData) {
            const currentRecordId = this.initialData._id || this.initialData.id;
            const recordId = record._id || record.id;
            if (currentRecordId === recordId) {
              return false;
            }
          }

          if (!record.billingPeriod || !record.billingPeriod.startDate || !record.billingPeriod.endDate) {
            return false;
          }

          const recordStart = normalizeDate(record.billingPeriod.startDate);
          const recordEnd = normalizeDate(record.billingPeriod.endDate);

          return newStart <= recordEnd && recordStart <= newEnd;
        });

        return {
          hasOverlap: overlappingRecords.length > 0,
          overlappingRecords,
          exactMatch: overlappingRecords.find((record) => {
            if (!record.billingPeriod || !record.billingPeriod.startDate || !record.billingPeriod.endDate) {
              return false;
            }

            const recordStart = normalizeDate(record.billingPeriod.startDate);
            const recordEnd = normalizeDate(record.billingPeriod.endDate);

            return newStart.getTime() === recordStart.getTime()
              && newEnd.getTime() === recordEnd.getTime();
          }),
        };
      } catch (error) {
        console.error('Error checking for overlapping records:', error);

        return {
          hasOverlap: false,
          overlappingRecords: [],
          error: error.message,
        };
      }
    },

    async handleOverlappingRecords(overlapCheck) {
      const { overlappingRecords, exactMatch } = overlapCheck;

      if (exactMatch) {
        const startDate = exactMatch.billingPeriod?.startDate
          ? this.formatDate(exactMatch.billingPeriod.startDate)
          : 'N/A';

        const endDate = exactMatch.billingPeriod?.endDate
          ? this.formatDate(exactMatch.billingPeriod.endDate)
          : 'N/A';

        const confirmed = await this.showConfirmDialog(
          'Existing Record Found',
          `An ${this.isDGMode ? 'DG' : 'electricity'} billing record already exists for the exact period ${startDate} to ${endDate}.\n\nWould you like to edit the existing record instead of creating a new one?`,
          'mdi-file-document-alert',
          'warning',
        );

        if (confirmed) {
          const editPath = this.isDGMode
            ? `/dg-billing-history/${exactMatch.id || exactMatch._id}`
            : `/billing-history/${exactMatch.id || exactMatch._id}`;

          this.$router.push(editPath);
        } else {
          this.reset();
        }

        return;
      }

      const overlapDetails = overlappingRecords.map((record) => {
        if (!record.billingPeriod || !record.billingPeriod.startDate || !record.billingPeriod.endDate) {
          return '• Invalid record (missing dates)';
        }

        const recordStart = new Date(record.billingPeriod.startDate);
        const recordEnd = new Date(record.billingPeriod.endDate);
        const newStart = new Date(this.billingPeriod.startDate);
        const newEnd = new Date(this.billingPeriod.endDate);

        let overlapType = '';

        if (newStart >= recordStart && newEnd <= recordEnd) {
          overlapType = ' (your period is inside this record)';
        } else if (newStart <= recordStart && newEnd >= recordEnd) {
          overlapType = ' (your period contains this record)';
        } else if (newStart < recordStart) {
          overlapType = ' (your period extends before this record)';
        } else {
          overlapType = ' (your period extends after this record)';
        }

        return `• ${this.formatDate(record.billingPeriod.startDate)} to ${this.formatDate(record.billingPeriod.endDate)}${overlapType}`;
      }).join('\n');

      await this.showOverlapResolutionDialog(overlappingRecords, overlapDetails);
    },

    async showOverlapResolutionDialog(overlappingRecords, overlapDetails) {
      if (overlappingRecords.length === 1) {
        const existingRecord = overlappingRecords[0];

        const startDate = existingRecord.billingPeriod?.startDate
          ? this.formatDate(existingRecord.billingPeriod.startDate)
          : 'N/A';

        const endDate = existingRecord.billingPeriod?.endDate
          ? this.formatDate(existingRecord.billingPeriod.endDate)
          : 'N/A';

        const confirmed = await this.showConfirmDialog(
          'Overlap Detected',
          `The selected period overlaps with an existing ${this.isDGMode ? 'DG' : 'electricity'} billing record:\n\n${overlapDetails}\n\nClick OK to DELETE existing record, or Cancel to choose different dates.`,
          'mdi-alert-circle',
          'warning',
        );

        if (confirmed) {
          const deleteConfirmed = await this.showConfirmDialog(
            'Delete Confirmation',
            `Are you sure you want to DELETE the existing billing record?\n\nPeriod: ${startDate} to ${endDate}\n\nThis action cannot be undone.`,
            'mdi-delete-alert',
            'error',
          );

          if (deleteConfirmed) {
            await this.deleteExistingRecord(existingRecord);
          } else {
            this.reset();
          }
        } else {
          this.reset();
        }

        return;
      }

      const action = await this.showConfirmDialog(
        'Multiple Overlaps Detected',
        `The selected period overlaps with ${overlappingRecords.length} existing ${this.isDGMode ? 'DG' : 'electricity'} billing records:\n\n${overlapDetails}\n\nClick OK to choose different dates, or Cancel to go to billing history.`,
        'mdi-alert-circle',
        'warning',
      );

      if (action) {
        this.reset();
      } else {
        this.$router.push(this.isDGMode ? '/dg-billing-history' : '/billing-history');
      }
    },

    async deleteExistingRecord(record) {
      try {
        if (this.isDGMode) {
          await this.$store.dispatch('dgBilling/deleteDGBillingRecord', record.id || record._id);
        } else {
          await this.$store.dispatch('billing/deleteBillingRecord', record.id || record._id);
        }

        this.$emit('show-message', {
          type: 'success',
          message: `Existing ${this.isDGMode ? 'DG' : 'electricity'} billing record deleted successfully. You can now proceed with the new entry.`,
        });
      } catch (error) {
        console.error('Error deleting existing record:', error);

        this.$emit('show-message', {
          type: 'error',
          message: 'Failed to delete existing record. Please try again or choose different dates.',
        });

        this.reset();
      }
    },

    showConfirmDialog(
      title,
      message,
      icon = 'mdi-information',
      iconColor = 'primary',
    ) {
      return new Promise((resolve) => {
        this.confirmDialog = {
          show: true,
          title,
          message,
          icon,
          iconColor,
          resolve,
        };
      });
    },

    handleDialogConfirm() {
      if (this.confirmDialog.resolve) {
        this.confirmDialog.resolve(true);
      }

      this.closeConfirmDialog();
    },

    handleDialogCancel() {
      if (this.confirmDialog.resolve) {
        this.confirmDialog.resolve(false);
      }

      this.closeConfirmDialog();
    },

    closeConfirmDialog() {
      this.confirmDialog = {
        show: false,
        title: '',
        message: '',
        icon: 'mdi-information',
        iconColor: 'primary',
        resolve: null,
      };
    },
  },
};
</script>

<style scoped>
.font-weight-bold input {
  font-weight: bold !important;
}

.time-of-use-consumption-card {
  border-radius: 12px;
  overflow: hidden;
}

.time-of-use-consumption-table >>> table {
  width: 100%;
}

.time-of-use-consumption-table >>> th,
.time-of-use-consumption-table >>> td {
  padding: 12px 10px;
  vertical-align: middle;
  border-bottom: 1px solid rgba(128, 128, 128, 0.15);
}

.time-of-use-consumption-table >>> th {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

/* Remove hardcoded white zebra stripe — let Vuetify theme handle row background */
.time-of-use-consumption-table >>> tbody tr:nth-child(even) {
  background: transparent;
}

.unit-label-cell {
  min-width: 220px;
}

.time-of-use-table-input {
  max-width: 160px;
  margin: 0 auto;
}

/* Theme-aware total row using currentColor opacity instead of hardcoded rgba */
.time-of-use-total-row {
  border-radius: 10px;
  padding: 14px 16px;
  background: rgba(128, 128, 128, 0.08);
  border: 1px solid rgba(128, 128, 128, 0.15);
}

/* Theme-aware chip using primary color with opacity */
.time-of-use-total-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  border-radius: 4px;
  font-weight: 700;
  letter-spacing: 0.04em;
  background: rgba(var(--v-primary-base), 0.15);
  color: var(--v-primary-base);
}
</style>
